<template>
    <div class="user-dashboard">
        <!-- 页面头部 -->
        <div class="header">
            <div class="header-left">
                <img src="@/assets/logo.png" alt="Logo" class="logo">
                <h1>小鱼智能控制台</h1>
            </div>
            
            <!-- 桌面端显示的按钮组 -->
            <div class="header-right-desktop">
                <el-button v-if="isAdmin" type="text" @click="goToAdminDashboard">
                    <i class="el-icon-s-platform"></i> 管理后台
                </el-button>
                <el-button type="text" @click="showCreateRobotDialog = true">
                    <i class="el-icon-plus"></i> 创建机器人
                </el-button>
                <el-button type="text" @click="handleUserProfile">
                    <i class="el-icon-user"></i> 个人信息
                </el-button>
                <el-button type="text" @click="handleGuide">
                    <i class="el-icon-document"></i> 接入指南
                </el-button>
                <el-button type="text" @click="handleAppDownload">
                    <i class="el-icon-download"></i> App下载
                </el-button>
                <el-button type="text" @click="handleLogout">
                    <i class="el-icon-switch-button"></i> 退出登录
                </el-button>
            </div>
            
            <!-- 移动端显示的下拉菜单 -->
            <div class="header-right-mobile">
                <!-- 编辑模式下的操作按钮 -->
                <div v-if="editMode" class="edit-mode-actions">
                    <el-button type="text" @click="selectAllRobots" class="select-all-btn">
                        {{ allSelected ? '取消全选' : '全选' }}
                    </el-button>
                    <el-button 
                        type="danger" 
                        :disabled="selectedRobots.length === 0"
                        @click="handleBatchDelete"
                        class="batch-delete-btn"
                    >
                        删除({{ selectedRobots.length }})
                    </el-button>
                    <el-button
                        v-if="layoutChanged"
                        type="success"
                        :loading="savingLayout"
                        @click="saveLayout"
                        class="save-layout-btn"
                    >
                        {{ savingLayout ? '保存中...' : '保存' }}
                    </el-button>
                    <el-button v-if="!layoutChanged" type="text" @click="exitEditMode" class="exit-edit-btn">
                        完成
                    </el-button>
                </div>
                <!-- 正常模式下的更多按钮 -->
                <el-dropdown v-else trigger="click" @command="handleMobileCommand">
                    <el-button class="mobile-menu-button" type="primary" size="small">
                        更多
                        <i class="el-icon-arrow-down el-icon--right"></i>
                    </el-button>
                    <el-dropdown-menu slot="dropdown" class="mobile-dropdown-menu">
                        <el-dropdown-item command="create" class="dropdown-item">
                            <i class="el-icon-plus"></i> 
                            <span>创建机器人</span>
                        </el-dropdown-item>
                        <el-dropdown-item command="profile" class="dropdown-item">
                            <i class="el-icon-user"></i> 
                            <span>个人信息</span>
                        </el-dropdown-item>
                        <el-dropdown-item command="guide" class="dropdown-item">
                            <i class="el-icon-document"></i> 
                            <span>接入指南</span>
                        </el-dropdown-item>
                        <el-dropdown-item command="download" class="dropdown-item">
                            <i class="el-icon-download"></i> 
                            <span>App下载</span>
                        </el-dropdown-item>
                        <el-dropdown-item command="edit" divided class="dropdown-item">
                            <i class="el-icon-edit"></i> 
                            <span>编辑模式</span>
                        </el-dropdown-item>
                        <el-dropdown-item command="logout" divided class="dropdown-item logout-item">
                            <i class="el-icon-switch-button"></i> 
                            <span>退出登录</span>
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
            </div>
        </div>

        <!-- 主要内容区域 -->
        <div class="main-content">
            <!-- Loading 遮罩 -->
            <div v-if="loading" class="loading-overlay">
                <div class="loading-spinner">
                    <i class="el-icon-loading" style="font-size: 48px; color: #409eff;"></i>
                    <p class="loading-text">加载中...</p>
                </div>
            </div>

            <!-- 创建机器人按钮 -->
            <div v-if="robots.length === 0 && !loading" class="empty-state">
                <div class="empty-icon">🤖</div>
                <p>您还没有创建任何机器人</p>
                <el-button type="primary" @click="showCreateRobotDialog = true">创建机器人</el-button>
            </div>

            <!-- 机器人列表 -->
            <div v-else class="robots-grid" :class="{ 'edit-dragging': editMode && dragInfo.dragging }">
                <div
                    v-for="(robot, index) in robots"
                    :key="robot.details.ID"
                    class="robot-card"
                    :class="{ 
                        'robot-offline': !robot.online,
                        'edit-mode': editMode,
                        'selected': selectedRobots.includes(robot.details.ID),
                        'robot-card-double': robot.details.device_type === 'double',
                        'robot-card-compatible': robot.details.device_type === 'compatible',
                        'dragging': dragInfo.dragging && dragInfo.dragRobotId === robot.details.ID
                    }"
                    @click="handleCardClick(robot)"
                    @touchstart.passive="handleTouchStart(robot, $event)"
                    @touchend.passive="handleTouchEnd"
                    @touchstart="handleDragTouchStart($event, robot, index)"
                    @touchmove="handleDragTouchMove($event, index)"
                    @touchend="handleDragTouchEnd"
                    @touchcancel="handleDragTouchEnd"
                >
                    <!-- 编辑模式下的选择框 -->
                    <div v-if="editMode" class="selection-checkbox">
                        <el-checkbox 
                            :value="selectedRobots.includes(robot.details.ID)"
                            @change="toggleRobotSelection(robot.details.ID)"
                            @click.stop
                        ></el-checkbox>
                    </div>

                    <!-- 快速操作按钮（右上角，仅在线时显示） -->
                    <div v-if="!editMode && robot.online" class="quick-controls">
                        <!-- 单开型机器人控制（包括兼容设备） -->
                        <template v-if="robot.details.device_type === 'single' || robot.details.device_type === 'compatible'">
                            <div 
                                :class="['quick-toggle-btn', {
                                    'active': robot.details.status === 'single_on'
                                }]"
                                @click.stop="toggleSingleRobot(robot)"
                            >
                                <i class="el-icon-switch-button"></i>
                            </div>
                        </template>
                        <!-- 双开型机器人控制 -->
                        <template v-else-if="robot.details.device_type === 'double'">
                            <div class="quick-double-controls">
                                <div
                                    :class="['quick-double-btn', 'left-btn', {
                                        'active': robot.details.status === 'double_left'
                                    }]"
                                    @click.stop="pressLeft(robot)"
                                >
                                    <i class="el-icon-arrow-left"></i>
                                </div>
                                <div
                                    :class="['quick-double-btn', 'right-btn', {
                                        'active': robot.details.status === 'double_right'
                                    }]"
                                    @click.stop="pressRight(robot)"
                                >
                                    <i class="el-icon-arrow-right"></i>
                                </div>
                            </div>
                        </template>
                    </div>

                    <!-- 卡片内容 -->
                    <div class="card-content">
                        <div class="card-header">
                            <span class="robot-name">{{ robot.details.name }}</span>
                        </div>

                        <!-- 状态指示器 - 移到左侧 -->
                        <div class="status-indicator">
                            <div class="status-dot" :class="robot.online ? 'online' : 'offline'"></div>
                            <span class="status-text">{{ robot.online ? '在线' : '离线' }}</span>
                        </div>

                        <div class="robot-info">
                            <p class="mqtt-topic">
                                <i class="el-icon-message"></i> 
                                {{ robot.details.mqtt_topic }}
                            </p>
                            <p class="device-type">
                                <i class="el-icon-sitemap"></i>
                                {{ robot.details.device_type === 'single' ? '单开型' : '双开型' }}
                            </p>
                        </div>

                        <!-- 桌面端操作按钮 -->
                        <div class="desktop-actions">
                            <el-button 
                                type="primary" 
                                size="small" 
                                @click.stop="goToRobotDetail(robot.details.ID)"
                                class="detail-btn"
                            >
                                详细控制
                            </el-button>
                            <el-button 
                                type="danger" 
                                size="small" 
                                @click.stop="deleteRobot(robot.details.ID, robot.details.name)"
                                class="delete-btn"
                            >
                                删除
                            </el-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 添加机器人按钮（当有机器人时也显示） -->
            <div v-if="robots.length > 0" class="add-robot">
                <el-button type="primary" @click="showCreateRobotDialog = true" class="create-btn">
                    <i class="el-icon-plus"></i> 创建新机器人
                </el-button>
            </div>
        </div>

        <!-- 创建机器人抽屉 -->
        <el-drawer
                title="创建机器人"
                :visible.sync="showCreateRobotDialog"
                size="65%"
                :direction="'btt'"
                :before-close="handleDialogClose"
        >
            <div class="form-container">
                <el-form
                        ref="robotForm"
                        :model="robotForm"
                        :rules="robotRules"
                        label-width="100px"
                        style="padding-top: 20px;"
                >
                    <el-form-item label="机器人名称" prop="name">
                        <el-input v-model="robotForm.name" placeholder="请输入机器人名称" class="form-input"/>
                    </el-form-item>
                    <el-form-item label="机器人主题" prop="mqttTopic">
                        <el-input v-model="robotForm.mqttTopic" placeholder="请输入机器人主题" class="form-input"/>
                    </el-form-item>
                    <el-form-item label="设备类型" prop="deviceType" class="device-type-form-item">
                        <el-radio-group v-model="robotForm.deviceType" class="radio-group">
                            <el-radio label="single">单开型</el-radio>
                            <el-radio label="double">双开型</el-radio>
                            <el-radio label="compatible">兼容旧设备</el-radio>
                        </el-radio-group>
                    </el-form-item>
                </el-form>
            </div>
            <div class="drawer-footer" style="margin-top: 30px; display: flex; justify-content: flex-end; gap: 10px;">
                <el-button @click="handleDialogClose">取消</el-button>
                <el-button type="primary" @click="handleCreateRobot" :loading="creatingRobot">
                    {{ creatingRobot ? '创建中...' : '创建' }}
                </el-button>
            </div>
        </el-drawer>
    </div>
</template>

<script>
import {controlRobot, createRobot, deleteRobot as deleteRobotApi, getUserRobots, batchDeleteRobots, updateRobotOrder} from '@/api/robot'
import {Message, MessageBox} from 'element-ui'

export default {
    name: 'UserDashboard',
    data() {
        return {
            robots: [],
            isProcessing: false,
            showCreateRobotDialog: false,
            creatingRobot: false,
            robotForm: {
                name: '',
                mqttTopic: '',
                deviceType: 'single'
            },
            robotRules: {
                name: [
                    {required: true, message: '请输入机器人名称', trigger: 'blur'},
                    {min: 1, max: 100, message: '名称长度在 1 到 100 个字符', trigger: 'blur'}
                ],
                mqttTopic: [
                    {required: true, message: '请输入MQTT协议主题', trigger: 'blur'},
                    {min: 1, max: 255, message: '主题长度在 1 到 255 个字符', trigger: 'blur'}
                ],
                deviceType: [
                    {required: true, message: '请选择设备类型', trigger: 'change'}
                ]
            },
            buttonStates: {},
            // 编辑模式相关
            editMode: false,
            selectedRobots: [],
            // 拖动排序相关
            layoutChanged: false,
            dragInfo: {
                dragging: false,
                dragRobotId: null,
                startIndex: -1,
                targetIndex: -1,
                startY: 0,
                currentY: 0,
                offsetY: 0,
                startX: 0,
                currentX: 0,
                offsetX: 0
            },
            savingLayout: false,
            controlTimer: null,
            loading: false,
            // 长按相关
            touchTimer: null,
            touchStartTime: 0
        }
    },
    computed: {
        username() {
            return this.$store.getters.username
        },
        isAdmin() {
            return this.$store.getters.isAdmin
        },
        allSelected() {
            return this.robots.length > 0 && this.selectedRobots.length === this.robots.length
        }
    },
    created() {
        this.fetchRobots()
        // 设置定时器定期刷新机器人状态
        this.controlTimer = setInterval(() => {
            this.fetchRobots()
        }, 5000) // 每5秒刷新一次
    },
    beforeDestroy() {
        // 清除定时器
        if (this.controlTimer) {
            clearInterval(this.controlTimer)
        }
    },
    methods: {
        // 跳转到管理后台
        goToAdminDashboard() {
            this.$router.push('/admin/dashboard')
        },
        
        // 处理App下载
        handleAppDownload() {
            this.$message.info('App下载功能开发中')
        },
        
        // 处理个人信息
        handleUserProfile() {
            this.$router.push('/user/details')
        },
        
        // 处理接入指南
        handleGuide() {
            // 检测是否为移动端
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768
            
            if (isMobile) {
                // 移动端在当前页面打开
                window.location.href = "https://doc.littlerook.top/doc"
            } else {
                // 桌面端在新页面打开
                window.open("https://doc.littlerook.top/doc")
            }
        },
        
        // 处理移动端下拉菜单命令
        handleMobileCommand(command) {
            switch(command) {
                case 'create':
                    this.showCreateRobotDialog = true
                    break
                case 'download':
                    this.handleAppDownload()
                    break
                case 'profile':
                    this.handleUserProfile()
                    break
                case 'guide':
                    this.handleGuide()
                    break
                case 'edit':
                    this.enterEditMode()
                    break
                case 'logout':
                    this.handleLogout()
                    break
            }
        },
        
        // 获取按钮状态
        getButtonState(robotId, buttonType) {
            console.log(1)
            console.log(this.buttonStates[robotId])
            return this.buttonStates[robotId] && this.buttonStates[robotId][buttonType] || false
        },

        // 获取用户的机器人列表
        fetchRobots(showLoading = false) {
            if (showLoading) {
                this.loading = true
            }
            getUserRobots().then(res => {
                this.robots = (res.data.list || []).map(robot => ({
                    ...robot,
                    checked: this.selectedRobots.includes(robot.details.ID)
                }))
            }).finally(() => {
                if (showLoading) {
                    this.loading = false
                }
            })
        },

        // 显示创建机器人对话框
        showCreateDialog() {
            this.robotForm = {
                name: '',
                mqttTopic: '',
                deviceType: 'single'
            }
            this.$refs.robotForm && this.$refs.robotForm.resetFields()
            this.showCreateRobotDialog = true
        },

        // 关闭对话框
        handleDialogClose() {
            this.$refs.robotForm && this.$refs.robotForm.resetFields()
            this.showCreateRobotDialog = false
        },

        // 创建机器人
        handleCreateRobot() {
            this.$refs.robotForm.validate(valid => {
                if (valid) {
                    this.creatingRobot = true
                    const {name, mqttTopic, deviceType} = this.robotForm
                    createRobot({
                        name,
                        mqttTopic,
                        deviceType
                    }).then(() => {
                        Message.success('机器人创建成功')
                        this.fetchRobots()
                        this.showCreateRobotDialog = false
                    }).finally(() => {
                        this.creatingRobot = false
                    })
                }
            })
        },

        // 删除机器人
        deleteRobot(id, name) {
            MessageBox.confirm(`确定要删除机器人"${name}"吗？`, '警告', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                deleteRobotApi(id).then(() => {
                    Message.success('机器人删除成功')
                    this.fetchRobots()
                }).catch(error => {
                    console.error('删除机器人失败:', error)
                    Message.error('删除机器人失败，请稍后重试')
                })
            }).catch(() => {
                // 用户取消删除
            })
        },

        // 控制机器人
        handleControlRobot(id, action) {
            const currentButtonStates = this.buttonStates
            this.isProcessing = true
            controlRobot(id, action).then(() => {
                const robot = this.robots.find(r => r.details.ID === id)
                if (robot) {
                    robot.details.status = action
                }
                this.buttonStates = currentButtonStates
            }).catch(error => {
                console.error('控制机器人失败:', error)
                Message.error('控制失败: ' + (error.response?.data?.message || '未知错误'))
            }).finally(() => {
                setTimeout(() => {
                    this.isProcessing = false
                }, 500)
            })
        },

        // 切换单开型机器人状态
        toggleSingleRobot(robot) {
            if (!robot.online) {
                Message.closeAll()
                Message.warning("机器人不在线")
                return
            }

            if (this.isProcessing) return

            const newAction = robot.details.status === 'single_on' ? 'off' : 'single_on'
            this.handleControlRobot(robot.details.ID, newAction)
        },

        // 按下左按钮
        pressLeft(robot) {
            if (!robot.online) {
                Message.warning("机器人不在线")
                return
            }

            if (this.isProcessing) return

            // 设置按钮按下状态
            if (!this.buttonStates[robot.details.ID]) {
                this.buttonStates[robot.details.ID] = {}
            }
            this.buttonStates[robot.details.ID].left = true

            this.handleControlRobot(robot.details.ID, 'double_left')

            // 1秒后恢复按钮状态
            const robotId = robot.details.ID
            setTimeout(() => {
                if (this.buttonStates && this.buttonStates[robotId]) {
                    this.buttonStates[robotId].left = false
                }
            }, 1000)
        },

        // 按下右按钮
        pressRight(robot) {
            if (!robot.online) {
                Message.warning("机器人不在线")
                return
            }

            if (this.isProcessing) return

            // 设置按钮按下状态
            if (!this.buttonStates[robot.details.ID]) {
                this.buttonStates[robot.details.ID] = {}
            }
            this.buttonStates[robot.details.ID].right = true

            this.handleControlRobot(robot.details.ID, 'double_right')

            // 1秒后恢复按钮状态
            const robotId = robot.details.ID
            setTimeout(() => {
                if (this.buttonStates && this.buttonStates[robotId]) {
                    this.buttonStates[robotId].right = false
                }
            }, 1000)
        },

        // 跳转到机器人详细控制页面
        goToRobotDetail(id) {
            this.$router.push(`/robot/${id}`)
        },

        // 退出登录
        handleLogout() {
            this.$confirm('您确定要退出登录吗？', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.$store.dispatch('LogOut').then(() => {
                    location.href = '/index';
                })
            }).catch(() => {});
        },

        // 编辑模式相关方法
        enterEditMode() {
            this.editMode = true
            this.selectedRobots = []
            // 暂停定时器刷新
            if (this.controlTimer) {
                clearInterval(this.controlTimer)
                this.controlTimer = null
            }
        },

        exitEditMode() {
            this.editMode = false
            this.selectedRobots = []
            this.layoutChanged = false
            // 恢复定时器刷新
            if (!this.controlTimer) {
                this.controlTimer = setInterval(() => {
                    this.fetchRobots()
                }, 5000)
            }
        },

        // 卡片点击处理
        handleCardClick(robot) {
            // 如果刚刚拖动过，不触发点击事件
            if (this.dragInfo.dragging) {
                return
            }
            
            if (this.editMode) {
                this.toggleRobotSelection(robot.details.ID)
            } else {
                this.goToRobotDetail(robot.details.ID)
            }
        },

        // 切换机器人选择状态
        toggleRobotSelection(robotId) {
            const index = this.selectedRobots.indexOf(robotId)
            if (index > -1) {
                this.selectedRobots.splice(index, 1)
            } else {
                this.selectedRobots.push(robotId)
            }

            // 更新 robots 数组中的 checked 状态
            this.robots.forEach(robot => {
                if (robot.details.ID === robotId) {
                    robot.checked = !robot.checked
                }
            })
        },

        // 全选/取消全选
        selectAllRobots() {
            if (this.allSelected) {
                this.selectedRobots = []
                this.robots.forEach(robot => {
                    robot.checked = false
                })
            } else {
                this.selectedRobots = this.robots.map(robot => robot.details.ID)
                this.robots.forEach(robot => {
                    robot.checked = true
                })
            }
        },

        // 批量删除
        handleBatchDelete() {
            if (this.selectedRobots.length === 0) {
                Message.warning('请选择要删除的机器人')
                return
            }

            const selectedNames = this.robots
                .filter(robot => this.selectedRobots.includes(robot.details.ID))
                .map(robot => robot.details.name)
                .join('、')

            MessageBox.confirm(
                `确定要删除以下机器人吗？\n${selectedNames}`, 
                '批量删除确认', 
                {
                    confirmButtonText: '确定删除',
                    cancelButtonText: '取消',
                    type: 'warning'
                }
            ).then(() => {
                batchDeleteRobots(this.selectedRobots).then(() => {
                    Message.success(`成功删除 ${this.selectedRobots.length} 个机器人`)
                    this.fetchRobots()
                    this.exitEditMode()
                })
            }).catch(() => {
                // 用户取消删除
            })
        },

        // 拖动排序相关方法
        handleDragTouchStart(e, robot, index) {
            if (!this.editMode) return
            
            this.dragInfo = {
                dragging: true,
                dragRobotId: robot.details.ID,
                startIndex: index,
                targetIndex: index,
                startY: e.touches[0].pageY,
                currentY: e.touches[0].pageY,
                offsetY: 0,
                startX: e.touches[0].pageX,
                currentX: e.touches[0].pageX,
                offsetX: 0
            }
        },

        handleDragTouchMove(e, index) {
            if (!this.editMode || !this.dragInfo.dragging) return
            
            // 只处理被拖动的卡片
            const currentRobot = this.robots[index]
            if (currentRobot.details.ID !== this.dragInfo.dragRobotId) {
                return
            }
            
            // 阻止默认的滚动行为
            if (e.preventDefault) {
                e.preventDefault()
            }
            if (e.stopPropagation) {
                e.stopPropagation()
            }
            
            this.dragInfo.currentY = e.touches[0].pageY
            this.dragInfo.offsetY = this.dragInfo.currentY - this.dragInfo.startY
            this.dragInfo.currentX = e.touches[0].pageX
            this.dragInfo.offsetX = this.dragInfo.currentX - this.dragInfo.startX
            
            // 判断当前主要移动方向
            const isHorizontalDominant = Math.abs(this.dragInfo.offsetX) > Math.abs(this.dragInfo.offsetY)
            
            // 计算目标索引（每移动约100px/120px交换一次位置）
            const cardHeight = 100
            const cardWidth = 120
            let moveCards = 0
            if (isHorizontalDominant) {
                const horizontalSteps = Math.round(this.dragInfo.offsetX / cardWidth)
                moveCards = horizontalSteps
            } else {
                moveCards = Math.round(this.dragInfo.offsetY / cardHeight)
            }

            let newTargetIndex = this.dragInfo.startIndex + moveCards
            
            // 限制在有效范围内
            newTargetIndex = Math.max(0, Math.min(this.robots.length - 1, newTargetIndex))
            
            // 只有当目标位置真正改变时才执行移动
            if (newTargetIndex !== this.dragInfo.targetIndex) {
                this.moveRobotToPosition(this.dragInfo.targetIndex, newTargetIndex)
                this.dragInfo.targetIndex = newTargetIndex
                this.layoutChanged = true
            }
            
            return false
        },

        handleDragTouchEnd() {
            const wasDragging = this.dragInfo.dragging
            
            // 重置拖动状态
            this.dragInfo = {
                dragging: false,
                dragRobotId: null,
                startIndex: -1,
                targetIndex: -1,
                startY: 0,
                currentY: 0,
                offsetY: 0,
                startX: 0,
                currentX: 0,
                offsetX: 0
            }
            
            // 如果拖动过，延迟一点时间以防止触发点击事件
            if (wasDragging) {
                setTimeout(() => {
                    // 确保状态已经重置
                }, 100)
            }
        },

        // 将机器人从当前位置移动到目标位置
        moveRobotToPosition(fromIndex, toIndex) {
            if (fromIndex === toIndex) return
            
            const robotsCopy = [...this.robots]
            const [movedRobot] = robotsCopy.splice(fromIndex, 1)
            robotsCopy.splice(toIndex, 0, movedRobot)
            this.robots = robotsCopy
        },

        // 保存布局
        saveLayout() {
            if (!this.layoutChanged) return
            
            this.savingLayout = true
            
            // 生成布局数据：最底下的order是0，越靠上order越大
            const totalCount = this.robots.length
            const layout = this.robots.map((robot, index) => {
                const order = totalCount - 1 - index
                return {
                    id: robot.details.ID,
                    order: order
                }
            })
            
            updateRobotOrder(layout).then(() => {
                Message.success('布局保存成功')
                this.layoutChanged = false
                this.fetchRobots()
                // 保存成功后退出编辑模式
                this.exitEditMode()
            }).catch(error => {
                console.error('保存布局失败:', error)
                Message.error('保存布局失败，请稍后重试')
            }).finally(() => {
                this.savingLayout = false
            })
        },

        // 长按相关方法
        handleTouchStart(robot) {
            if (this.editMode) return
            
            this.touchStartTime = Date.now()
            this.touchTimer = setTimeout(() => {
                // 长按超过500ms进入编辑模式
                this.enterEditMode()
                this.toggleRobotSelection(robot.details.ID)
                // 添加触觉反馈（如果支持）
                if (navigator.vibrate) {
                    navigator.vibrate(50)
                }
            }, 500)
        },

        handleTouchEnd() {
            if (this.touchTimer) {
                clearTimeout(this.touchTimer)
                this.touchTimer = null
            }
        }
    }
}
</script>

<style scoped>

.user-dashboard {
    min-height: 100vh;
    background-color: #f5f7fa;
}

.header {
    padding: 20px;
    display: flex;
    background-color: white;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eaeaea;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 15px;
}

.logo {
    width: 40px;
    height: 40px;
    border-radius: 4px;
}

.header h1 {
    margin: 0;
    color: #303133;
}

.header-right-desktop {
    display: flex;
    align-items: center;
    gap: 10px;
}

.header-right-mobile {
    display: none;
}

/* 移动端菜单按钮样式 - Element UI风格 */
.mobile-menu-button {
    border-radius: 6px !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    padding: 8px 16px !important;
    height: auto !important;
    line-height: 1.4 !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2) !important;
    border: 1px solid #409eff !important;
    background-color: #409eff !important;
    color: white !important;
}

.mobile-menu-button:hover {
    background-color: #66b1ff !important;
    border-color: #66b1ff !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3) !important;
}

.mobile-menu-button:active {
    transform: translateY(0) !important;
    box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2) !important;
}

.mobile-menu-button:focus {
    outline: none !important;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2) !important;
}

.mobile-menu-button .el-icon-arrow-down {
    margin-left: 4px !important;
    font-size: 12px !important;
    transition: transform 0.3s ease !important;
}

.mobile-menu-button:hover .el-icon-arrow-down {
    transform: rotate(180deg) !important;
}

/* 移动端下拉菜单样式 */
.mobile-dropdown-menu {
    border-radius: 16px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
    border: none !important;
    padding: 8px 0 !important;
    margin-top: 8px !important;
    backdrop-filter: blur(20px);
    background: rgba(255, 255, 255, 0.95) !important;
    min-width: 200px !important;
}

.mobile-dropdown-menu .el-dropdown-menu__item {
    padding: 12px 20px !important;
    border-radius: 0 !important;
    transition: all 0.3s ease !important;
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    font-size: 15px !important;
    color: #303133 !important;
    position: relative !important;
    overflow: hidden !important;
}

.mobile-dropdown-menu .el-dropdown-menu__item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transition: width 0.3s ease;
    z-index: -1;
}

.mobile-dropdown-menu .el-dropdown-menu__item:hover {
    background: transparent !important;
    color: white !important;
    transform: translateX(4px);
}

.mobile-dropdown-menu .el-dropdown-menu__item:hover::before {
    width: 100%;
}

.mobile-dropdown-menu .el-dropdown-menu__item i {
    font-size: 16px !important;
    width: 20px !important;
    text-align: center !important;
    transition: all 0.3s ease !important;
}

.mobile-dropdown-menu .el-dropdown-menu__item:hover i {
    transform: scale(1.1);
}

.mobile-dropdown-menu .el-dropdown-menu__item span {
    font-weight: 500 !important;
    transition: all 0.3s ease !important;
}

/* 分割线样式 */
.mobile-dropdown-menu .el-dropdown-menu__item--divided {
    border-top: 1px solid rgba(0, 0, 0, 0.08) !important;
    margin-top: 4px !important;
    padding-top: 16px !important;
}

/* 退出登录特殊样式 */
.mobile-dropdown-menu .logout-item {
    color: #f56c6c !important;
}

.mobile-dropdown-menu .logout-item:hover {
    color: white !important;
}

.mobile-dropdown-menu .logout-item::before {
    background: linear-gradient(135deg, #f56c6c 0%, #f78989 100%) !important;
}

/* 编辑模式操作按钮样式 */
.edit-mode-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.edit-mode-actions .el-button {
    font-size: 14px;
    padding: 8px 12px;
}

.select-all-btn {
    color: #409eff;
}

.batch-delete-btn {
    background-color: #f56c6c;
    border-color: #f56c6c;
    color: white;
}

.batch-delete-btn:disabled {
    background-color: #f5f7fa;
    border-color: #dcdfe6;
    color: #c0c4cc;
}

.exit-edit-btn {
    color: #67c23a;
}

.save-layout-btn {
    background-color: #67c23a;
    border-color: #67c23a;
    color: white;
}

.save-layout-btn:disabled {
    background-color: #f5f7fa;
    border-color: #dcdfe6;
    color: #c0c4cc;
}

.header-right-desktop .el-button,
.header-right-mobile .el-button {
    color: #606266;
}

.header-right-desktop .el-button i,
.header-right-mobile .el-button i {
    margin-right: 5px;
}

.main-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;    
}

/* Loading 样式 */
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.loading-text {
    font-size: 16px;
    color: #606266;
    font-weight: 500;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.empty-state p {
    color: #606266;
    margin-bottom: 24px;
    font-size: 16px;
}

.robots-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin-bottom: 20px;
}

.robots-grid.edit-dragging {
    touch-action: none;
    -webkit-overflow-scrolling: auto;
}

.robot-card {
    position: relative;
    background: white;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    border: 2px solid transparent;
}

.robot-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
}

.robot-card-double {
    grid-column: span 2;
}

.robot-card-compatible {
    background-color: #fffae3;
}

.robot-offline {
    opacity: 0.6;
}

.robot-card.edit-mode {
    border-color: #e4e7ed;
    touch-action: none;
    -webkit-user-select: none;
    user-select: none;
}

.robot-card.selected {
    border-color: #409eff;
    background-color: #f0f9ff;
}

.robot-card.dragging {
    opacity: 0.9;
    transform: scale(1.08) rotate(2deg);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    z-index: 999;
    transition: none !important;
    border-color: #409eff !important;
}

/* 选择框样式 */
.selection-checkbox {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 10;
}

/* 快速操作按钮样式 */
.quick-controls {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
    /* 确保在移动端有足够的层级 */
    pointer-events: auto;
}

.quick-toggle-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(64, 158, 255, 0.1);
    border: 1px solid #d6d6d6;
    color: #409eff;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    box-shadow: none;
    backdrop-filter: blur(10px);
    /* 移动端触摸优化 */
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    -webkit-user-select: none;
    user-select: none;
    /* 确保按钮可以正确接收点击事件 */
    pointer-events: auto;
}

.quick-toggle-btn:hover {
    background-color: #409eff;
    color: white;
    transform: scale(1.05);
    box-shadow: none;
}

.quick-toggle-btn:active {
    transform: scale(0.95);
}

.quick-toggle-btn.active {
    border: 1px solid #409eff;
    background-color: #409eff;
    color: white;
    box-shadow: none;
    animation: pulse 2s infinite;
}

/* 强制重置非激活状态的样式，确保移动端正确显示 */
.quick-toggle-btn:not(.active) {
    background-color: rgba(64, 158, 255, 0.1) !important;
    border: 1px solid #d6d6d6 !important;
    color: #409eff !important;
    box-shadow: none !important;
    animation: none !important;
}

@keyframes pulse {
    0% {
        box-shadow: none;
    }
    50% {
        box-shadow: none;
    }
    100% {
        box-shadow: none;
    }
}

.quick-double-controls {
    display: flex;
    gap: 6px;
}

.quick-double-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(64, 158, 255, 0.1);
    border: 1px solid #d6d6d6;
    color: #409eff;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    box-shadow: none;
    backdrop-filter: blur(10px);
    font-size: 14px;
    /* 移动端触摸优化 */
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    -webkit-user-select: none;
    user-select: none;
    /* 确保按钮可以正确接收点击事件 */
    pointer-events: auto;
}

.quick-double-btn:hover {
    background-color: #409eff;
    color: white;
    transform: scale(1.05);
    box-shadow: none;
}

.quick-double-btn:active {
    transform: scale(0.95);
}

.quick-double-btn.active {
    border: 1px solid #409eff;
    background-color: #409eff;
    color: white;
    box-shadow: none;
    animation: pulse 2s infinite;
}

/* 强制重置双开按钮非激活状态的样式，确保移动端正确显示 */
.quick-double-btn:not(.active) {
    background-color: rgba(64, 158, 255, 0.1) !important;
    border: 1px solid #d6d6d6 !important;
    color: #409eff !important;
    box-shadow: none !important;
    animation: none !important;
}

.card-content {
    margin-top: 8px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.robot-name {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    flex: 1;
    margin-right: 8px;
}

/* 状态指示器样式 */
.status-indicator {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.status-dot.online {
    background-color: #67c23a;
    box-shadow: 0 0 6px rgba(103, 194, 58, 0.4);
}

.status-dot.offline {
    background-color: #f56c6c;
    box-shadow: 0 0 6px rgba(245, 108, 108, 0.4);
}

.status-text {
    font-size: 12px;
    color: #606266;
    font-weight: 500;
}

.robot-info {
    margin-bottom: 0;
}

.robot-info p {
    margin: 6px 0;
    color: #606266;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
}

.robot-info i {
    font-size: 14px;
    color: #909399;
}

/* 桌面端操作按钮样式 */
.desktop-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
}

.desktop-actions .detail-btn {
    flex: 1;
    background-color: #409eff;
    border-color: #409eff;
    color: white;
    transition: all 0.3s ease;
}

.desktop-actions .detail-btn:hover {
    background-color: #66b1ff;
    border-color: #66b1ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.desktop-actions .delete-btn {
    flex: 1;
    background-color: #f56c6c;
    border-color: #f56c6c;
    color: white;
    transition: all 0.3s ease;
}

.desktop-actions .delete-btn:hover {
    background-color: #f78989;
    border-color: #f78989;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(245, 108, 108, 0.3);
}

/* 移除旧的按钮样式，使用新的快速操作按钮样式 */

.add-robot {
    text-align: center;
    margin-top: 30px;
}

.create-btn {
    padding: 12px 24px;
    font-size: 16px;
}

/* 表单样式优化 */
.form-container {
    max-width: 500px;
    margin: 0 auto;
    padding: 0 20px;
}

.form-input {
    max-width: 300px;
    width: 100%;
    height: 40px;
    border-radius: 4px;
}

.device-type-form-item {
    display: flex;
    align-items: center;
}

.radio-group {
    display: flex;
    gap: 20px;
}

.drawer-footer {
    padding: 0 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .header-left {
        align-self: flex-start;
    }

    .header-right-desktop {
        display: none;
    }

    .header-right-mobile {
        display: block;
        align-self: flex-end;
    }

    /* 移动端菜单按钮调整 */
    .mobile-menu-button {
        padding: 6px 12px !important;
        font-size: 13px !important;
        border-radius: 4px !important;
    }

    .mobile-menu-button .el-icon-arrow-down {
        font-size: 11px !important;
        margin-left: 3px !important;
    }

    /* 移动端下拉菜单调整 */
    .mobile-dropdown-menu {
        min-width: 180px !important;
        margin-top: 6px !important;
    }

    .mobile-dropdown-menu .el-dropdown-menu__item {
        padding: 14px 18px !important;
        font-size: 14px !important;
        gap: 10px !important;
    }

    .mobile-dropdown-menu .el-dropdown-menu__item i {
        font-size: 15px !important;
        width: 18px !important;
    }

    .logo {
        width: 32px;
        height: 32px;
    }

    .header h1 {
        font-size: 20px;
    }

    /* 移动端机器人网格布局 - 每行2个 */
    .robots-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }

    .main-content {
        padding: 12px;
    }

    /* 移动端卡片样式优化 */
    .robot-card {
        padding: 12px;
        border-radius: 10px;
        min-height: 120px;
    }

    /* 移动端双开型机器人卡片占满一行 */
    .robot-card-double {
        grid-column: span 2;
    }

    /* 移动端拖动效果更流畅 */
    .robot-card.dragging {
        opacity: 0.95;
        transform: scale(1.1) rotate(3deg);
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
        transition: none !important;
    }

    .robot-name {
        font-size: 14px;
        font-weight: 600;
    }

    .status-indicator {
        margin-bottom: 8px;
    }

    .status-dot {
        width: 6px;
        height: 6px;
    }

    .status-text {
        font-size: 11px;
    }

    .robot-info p {
        font-size: 12px;
        margin: 4px 0;
    }

    .robot-info i {
        font-size: 12px;
    }

    /* 移动端隐藏桌面端操作按钮 */
    .desktop-actions {
        display: none;
    }

    /* 快速操作按钮在移动端的调整 */
    .quick-controls {
        top: 6px;
        right: 6px;
        z-index: 15; /* 移动端提高层级 */
    }

    .quick-toggle-btn {
        width: 40px;
        height: 40px;
        font-size: 16px;
        /* 移动端触摸优化 */
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        /* 确保移动端active状态正确重置 */
        -webkit-user-select: none;
        user-select: none;
    }

    /* 移动端快速按钮的active状态优化 */
    .quick-toggle-btn:active {
        transform: scale(0.9);
        transition: transform 0.1s ease;
    }

    /* 确保移动端active状态在触摸结束后正确重置 */
    .quick-toggle-btn.active:active {
        transform: scale(0.9);
    }

    /* 强制重置移动端非激活状态的样式 */
    .quick-toggle-btn:not(.active) {
        background-color: rgba(64, 158, 255, 0.1) !important;
        border: 1px solid #d6d6d6 !important;
        color: #409eff !important;
        animation: none !important;
    }

    /* 移动端触摸结束后强制重置样式 */
    .quick-toggle-btn:not(.active):active {
        background-color: rgba(64, 158, 255, 0.1) !important;
        border: 1px solid #d6d6d6 !important;
        color: #409eff !important;
    }

    .quick-double-btn {
        width: 32px;
        height: 32px;
        font-size: 12px;
        /* 移动端触摸优化 */
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        /* 确保移动端active状态正确重置 */
        -webkit-user-select: none;
        user-select: none;
    }

    /* 移动端双开按钮的active状态优化 */
    .quick-double-btn:active {
        transform: scale(0.9);
        transition: transform 0.1s ease;
    }

    /* 确保移动端active状态在触摸结束后正确重置 */
    .quick-double-btn.active:active {
        transform: scale(0.9);
    }

    /* 强制重置移动端双开按钮非激活状态的样式 */
    .quick-double-btn:not(.active) {
        background-color: rgba(64, 158, 255, 0.1) !important;
        border: 1px solid #d6d6d6 !important;
        color: #409eff !important;
        box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2) !important;
        animation: none !important;
    }

    /* 移动端双开按钮触摸结束后强制重置样式 */
    .quick-double-btn:not(.active):active {
        background-color: rgba(64, 158, 255, 0.1) !important;
        border: 1px solid #d6d6d6 !important;
        color: #409eff !important;
    }

    .quick-double-controls {
        gap: 4px;
    }

    /* 选择框在移动端的调整 */
    .selection-checkbox {
        top: 8px;
        left: 8px;
    }

    /* 编辑模式操作按钮在移动端的调整 */
    .edit-mode-actions {
        gap: 6px;
    }

    .edit-mode-actions .el-button {
        font-size: 12px;
        padding: 6px 10px;
    }

    /* 表单响应式样式 */
    .form-container {
        padding: 0 15px;
    }

    .form-input {
        max-width: 100%;
    }

    .radio-group {
        flex-direction: column;
        gap: 10px;
    }

    .device-type-form-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }

    .drawer-footer {
        padding: 0 15px;
        flex-direction: row;
        gap: 10px;
    }

    .drawer-footer .el-button {
        flex: 1;
        min-width: 0;
    }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
    .robots-grid {
        gap: 8px;
    }

    .robot-card {
        padding: 10px;
        min-height: 110px;
    }

    .robot-name {
        font-size: 13px;
    }

    .status-dot {
        width: 5px;
        height: 5px;
    }

    .status-text {
        font-size: 10px;
    }

    .robot-info p {
        font-size: 11px;
    }

    .quick-toggle-btn {
        width: 36px;
        height: 36px;
        font-size: 14px;
        /* 超小屏幕触摸优化 */
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        -webkit-user-select: none;
        user-select: none;
    }

    .quick-double-btn {
        width: 36px;
        height: 36px;
        font-size: 14px;
        /* 超小屏幕触摸优化 */
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        -webkit-user-select: none;
        user-select: none;
    }

    /* 超小屏幕菜单按钮调整 */
    .mobile-menu-button {
        padding: 5px 10px !important;
        font-size: 12px !important;
        border-radius: 3px !important;
    }

    .mobile-menu-button .el-icon-arrow-down {
        font-size: 10px !important;
        margin-left: 2px !important;
    }

    /* 超小屏幕下拉菜单调整 */
    .mobile-dropdown-menu {
        min-width: 160px !important;
    }

    .mobile-dropdown-menu .el-dropdown-menu__item {
        padding: 12px 16px !important;
        font-size: 13px !important;
        gap: 8px !important;
    }

    .mobile-dropdown-menu .el-dropdown-menu__item i {
        font-size: 14px !important;
        width: 16px !important;
    }
}
</style>